import { type ReactNode } from 'react';
import { isEqual } from 'lodash';
import type toast from 'react-hot-toast/headless';
import { useToast as getUseToast } from '@hungryroot/components-toast';
import {
  Cart,
  ChefHat,
  Clock,
  Coin2,
  Fish,
  Flexitarian,
  Fridge,
  Heart,
  Person,
  StarOutline,
  Vegan2,
  Vegetarian2,
} from '@hungryroot/icons';
import { EatingHabit, Goals } from '../../defs/quiz';
import {
  CHILDREN_COUNT_PROP,
  HABITS_QUESTION_PROPS,
  YOUNG_CHILDREN_COUNT_PROP,
} from '../../questionProps';
import { type Quiz, type NonNullableQuiz } from '../../quiz-hooks';

export enum QuizToastVariant {
  Children,
  SaveTime,
  ImproveHealth,
  LoseWeight,
  TryNewThings,
  EatLessTakeout,
  GroceryShopLess,
  WasteLessFood,
  EatMoreVeggiesWithFamily,
  SimplifyCooking,
  SaveMoney,
  Flexitarian,
  Pescatarian,
  Vegetarian,
  Vegan,
  Protein,
}

export interface QuizToastObj {
  content: {
    text: string;
    icon: ReactNode;
  };
  variant: QuizToastVariant;
  toast: (typeof toast)['custom'];
  clearToasts: (typeof toast)['dismiss'];
}

const GOALS_SORT_ORDER = {
  1: Goals.ImproveHealth,
  2: Goals.LoseWeight,
  3: Goals.SaveTime,
  4: Goals.SimplifyCooking,
  5: Goals.SaveMoney,
  6: Goals.GroceryShopLess,
  7: Goals.WasteLessFood,
  8: Goals.TryNewThings,
};

function getToastContent(variant: QuizToastVariant) {
  switch (variant) {
    case QuizToastVariant.Children:
      return {
        icon: <Person />,
        text: 'Shop from 300+ kid-friendly recipes that are as healthy as they are tasty.',
      };
    case QuizToastVariant.SaveTime:
      return {
        icon: <Clock />,
        text: 'The average customer saves 5 hours per week on grocery shopping, meal prepping + cooking.',
      };
    case QuizToastVariant.ImproveHealth:
      return {
        icon: <Heart />,
        text: 'The average customer eats 10 lbs of veggies in their first month with us.',
      };
    case QuizToastVariant.GroceryShopLess:
      return {
        icon: <Cart />,
        text: 'The average customer saves over 1 hour each week on grocery shopping.',
      };
    case QuizToastVariant.WasteLessFood:
      return {
        icon: <Fridge />,
        text: 'Our groceries come with easy recipes that put all your food to use.',
      };
    case QuizToastVariant.TryNewThings:
      return {
        icon: <StarOutline />,
        text: 'We add 100s of recipes each month, so there’s always something tasty to discover.',
      };
    case QuizToastVariant.LoseWeight:
      return {
        icon: <StarOutline />,
        text: 'Shop 1,000s of recipes that are high-protein, lower-sugar, or <500 calories per serving. ',
      };
    case QuizToastVariant.EatMoreVeggiesWithFamily:
      return {
        icon: <Person />,
        text: 'The average customer eats 10 lbs of veggies in their first month with us.',
      };
    case QuizToastVariant.EatLessTakeout:
      return {
        icon: <ChefHat />,
        text: 'The average customer saves $37 each week on takeout and delivery.',
      };
    case QuizToastVariant.SimplifyCooking:
      return {
        icon: <ChefHat />,
        text: '150+ ingredients are already chopped or marinated to make recipes even easier.',
      };
    case QuizToastVariant.SaveMoney:
      return {
        icon: <Coin2 />,
        text: 'The average customer saves $37 each week on takeout and delivery.',
      };
    case QuizToastVariant.Flexitarian:
      return {
        icon: <Flexitarian />,
        text: 'Nice! We’ve got 1,000+ flexitarian recipes for you to try.',
      };
    case QuizToastVariant.Pescatarian:
      return {
        icon: <Fish />,
        text: 'Nice! We’ve got 1,000+ pescatarian recipes for you to try.',
      };
    case QuizToastVariant.Vegetarian:
      return {
        icon: <Vegetarian2 />,
        text: 'Nice! We’ve got 1,000+ vegetarian recipes for you to try.',
      };
    case QuizToastVariant.Vegan:
      return {
        icon: <Vegan2 />,
        text: 'Nice! We’ve got 1,000+ plant-based recipes for you to try.',
      };
    case QuizToastVariant.Protein:
      return {
        icon: <Person />,
        text: 'Tasty! You can always update this later in your Food Profile.',
      };
  }
}

function getIsIncrease<K extends keyof Quiz>(
  question: K,
  previousQuiz: Quiz | undefined,
  value: NonNullableQuiz[K]
) {
  const isNumber = typeof value === 'number';

  switch (question) {
    case YOUNG_CHILDREN_COUNT_PROP:
      return (
        isNumber &&
        (previousQuiz?.youngChildrenCount === undefined ||
          previousQuiz?.youngChildrenCount === null ||
          previousQuiz.youngChildrenCount < value)
      );
    default:
      return (
        isNumber &&
        (previousQuiz?.childrenCount === undefined ||
          previousQuiz?.childrenCount === null ||
          previousQuiz.childrenCount < value)
      );
  }
}

function getToastVariant<K extends keyof Quiz>(
  previousQuiz: Quiz | undefined,
  question: K,
  value: NonNullableQuiz[K],
  quiz: Quiz
) {
  if (
    question === CHILDREN_COUNT_PROP ||
    question === YOUNG_CHILDREN_COUNT_PROP
  ) {
    if (!getIsIncrease(question, previousQuiz, value)) {
      return;
    }
    return QuizToastVariant.Children;
  }

  if (question === 'goals' && Array.isArray(value) && value.length) {
    if (
      previousQuiz?.goals !== null &&
      previousQuiz?.goals?.length &&
      previousQuiz.goals.length > value.length
    ) {
      return;
    }

    const sortedGoals = value
      .map((goal) => {
        const priority = Object.keys(GOALS_SORT_ORDER).find(
          (key) => GOALS_SORT_ORDER[key as keyof typeof goal] === goal
        );

        return {
          goal,
          priority: priority !== undefined ? parseInt(priority) : -1,
        };
      })
      .sort((a, b) => a.priority - b.priority);

    const { goal: highestPriorityGoal } = sortedGoals[0];
    const indexOfValue = Object.values(Goals).indexOf(
      highestPriorityGoal as Goals
    );
    const key = Object.keys(Goals)[indexOfValue];
    return QuizToastVariant[key as keyof typeof QuizToastVariant];
  }

  if (question === HABITS_QUESTION_PROPS && typeof value === 'string') {
    const key = value.charAt(0).toUpperCase() + value.slice(1);
    return QuizToastVariant[key as keyof typeof QuizToastVariant];
  }

  const showPlantProteinToast =
    quiz.habits === EatingHabit.Vegan || quiz.habits === EatingHabit.Vegetarian;
  if (
    question === 'meats' ||
    (question === 'seafood' && quiz.habits === EatingHabit.Pescatarian) ||
    (question === 'plants' && showPlantProteinToast)
  ) {
    return QuizToastVariant.Protein;
  }
  return;
}

function quizResponseDiff<K extends keyof Quiz>(
  quiz: Quiz,
  previousQuiz: Quiz
) {
  let diffKey: K | null = null;
  Object.keys(quiz).forEach((key) => {
    if (!isEqual(quiz[key as K], previousQuiz[key as K])) {
      return (diffKey = key as K);
    }
  });
  return diffKey !== null
    ? {
        key: diffKey,
        quizResponse: {
          [diffKey]: quiz[diffKey],
        },
        previousQuizResponse: {
          [diffKey]: previousQuiz[diffKey],
        },
      }
    : diffKey;
}

export function getQuizToast(
  quiz: Quiz,
  previousQuiz: Quiz | undefined,
  skipToasts?: {
    [key: string]: boolean;
  }
): QuizToastObj | undefined {
  const { dismiss, toast } = getUseToast();

  const diff = previousQuiz ? quizResponseDiff(quiz, previousQuiz) : null;

  if (diff === null) {
    return;
  }

  const { key } = diff;

  const shouldSkip = skipToasts !== undefined && skipToasts[key];
  if (shouldSkip) {
    return;
  }

  const variant = getToastVariant(previousQuiz, key, quiz[key], quiz);

  if (variant === undefined) {
    return;
  }

  const content = getToastContent(variant);

  return {
    variant,
    content,
    toast: toast,
    clearToasts: dismiss,
  };
}
